import {useCallback, useMemo} from 'react';

import {IndustryLevel, InputMaybe} from 'modules/decisionsNext/api/types';
import {UseTreeData} from 'modules/decisionsNext/hooks/trees/types';

import {
  filterTree,
  GenericTreeNode,
  groupSelectedKeys,
  groupSelectedKeysByLevel,
  hasChildren,
  iterateTree,
  transformTree,
  ungroupSelectedKeys,
} from 'utils/treeUtils';
import {useIndustryTreeQuery} from 'modules/decisionsNext/api/trees/industryTree.generated';
import {industryFilterKeys} from 'core/constants/policyFilter';

import {selectedByLevelToFilter} from 'components/TreePicker/utils/filterTreePickerUtils';
import {useOptimizedSelection} from 'components/Tree/hooks/useOptimizedSelection';
import {TreeBasicNode} from 'components/Tree/TreeTypes';

export type IndustryNode = GenericTreeNode<TreeBasicNode & {naics: string; level: IndustryLevel}>;

export const useIndustryTree: UseTreeData<
  string,
  IndustryNode,
  {
    skip?: boolean;
    filterXXNaics?: boolean;
  }
> = ({skip, filterXXNaics} = {filterXXNaics: false}) => {
  const {data, loading} = useIndustryTreeQuery({
    skip,
    disableSkipSetsLoadingState: true,
  });

  const treeData = useMemo(() => {
    const transformedData = transformTree(data?.industries?.tree ?? [], item => ({
      title: item.name,
      key: item.id as (typeof industryFilterKeys)[number],
      naics: item.naics,
      level: item.level,
    }));

    if (filterXXNaics) {
      return filterTree([...transformedData], item => !item.naics.endsWith('xx'));
    }

    return transformedData;
  }, [data?.industries?.tree, filterXXNaics]);

  const treeMap = useMemo(() => {
    const map = new Map<string, IndustryNode>();

    iterateTree(
      treeData,
      (node, parent) => {
        map.set(node.key, {
          key: node.key,
          title: node.title,
          naics: node.naics,
          parentKey: parent?.key,
          level: node.level,
          childrenCount: hasChildren(node) ? node.children.length : undefined,
        });
      },
      {withParent: true}
    );

    return map;
  }, [treeData]);

  return {
    treeMap,
    treeData,
    loading,
  };
};

type Selection = {
  industryGroupIds?: InputMaybe<string[]>;
  industryIds?: InputMaybe<string[]>;
  industrySubGroupIds?: InputMaybe<string[]>;
};

export function useIndustryTreeSelection({
  treeData,
  selection,
  mergeSelection,
  showAllSelectionCount,
}: {
  treeData: IndustryNode[];
  selection: Selection;
  mergeSelection: (selection: Selection) => void;
  showAllSelectionCount?: boolean;
}) {
  const {industryGroupIds, industryIds, industrySubGroupIds} = selection;
  const selected = useMemo(() => {
    const allIds = [industryGroupIds, industryIds, industrySubGroupIds].flatMap(group => group || []);
    return Array.from(ungroupSelectedKeys(treeData, allIds));
  }, [industryGroupIds, industryIds, industrySubGroupIds, treeData]);

  const setSelected = useCallback(
    (newSelected: string[]) => {
      const groupedSelected = groupSelectedKeys(treeData, newSelected);
      const selectedByLevel = groupSelectedKeysByLevel(treeData, groupedSelected);
      mergeSelection(
        selectedByLevelToFilter(selectedByLevel, ['industryGroupIds', 'industrySubGroupIds', 'industryIds'])
      );
    },
    [mergeSelection, treeData]
  );

  const setSelectedNone = useCallback(() => {
    mergeSelection({
      industryGroupIds: undefined,
      industrySubGroupIds: undefined,
      industryIds: undefined,
    });
  }, [mergeSelection]);

  return {
    ...useOptimizedSelection({
      selected,
      setSelected,
      setSelectedNone,
      treeData,
      showAllSelectionCount,
    }),
    selectedNoneData: {
      industryGroupIds: undefined,
      industrySubGroupIds: undefined,
      industryIds: undefined,
    },
  };
}
